import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, Sparkles, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { cn } from '@/lib/utils';

interface QuickUploadZoneProps {
  onFileSelect: (file: File) => void;
  isProcessing?: boolean;
  processingProgress?: number;
  maxSize?: number;
  className?: string;
}

interface ProcessingStatus {
  stage: 'idle' | 'uploading' | 'parsing' | 'processing' | 'completed' | 'error';
  progress: number;
  message: string;
  error?: string;
}

const STAGE_CONFIGS = {
  idle: {
    icon: Upload,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  uploading: {
    icon: Upload,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  parsing: {
    icon: FileText,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  processing: {
    icon: Sparkles,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  error: {
    icon: AlertCircle,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  }
};

export function QuickUploadZone({
  onFileSelect,
  isProcessing = false,
  processingProgress = 0,
  maxSize = 50 * 1024 * 1024, // 50MB
  className
}: QuickUploadZoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hoveredFile, setHoveredFile] = useState<File | null>(null);

  const currentStage = isProcessing
    ? processingProgress < 25 ? 'uploading'
    : processingProgress < 50 ? 'parsing'
    : processingProgress < 100 ? 'processing'
    : 'completed'
    : 'idle';

  const stageConfig = STAGE_CONFIGS[currentStage];
  const CurrentIcon = stageConfig.icon;

  // File validation
  const validateFile = (file: File): boolean => {
    const validExtensions = ['.ofx'];
    const hasValidExtension = validExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!hasValidExtension) {
      return false;
    }

    if (file.size > maxSize) {
      return false;
    }

    return true;
  };

  // File handlers
  const handleFileSelect = (file: File) => {
    if (validateFile(file)) {
      onFileSelect(file);
      setHoveredFile(null);
    } else {
      const errorMessage = !file.name.toLowerCase().endsWith('.ofx')
        ? 'Por favor, selecione um arquivo .ofx válido'
        : 'Arquivo muito grande. O tamanho máximo é 50MB';

      // Show error feedback
      setTimeout(() => setHoveredFile(null), 2000);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const ofxFile = files.find(file =>
      file.name.toLowerCase().endsWith('.ofx')
    );

    if (ofxFile) {
      setIsDragOver(true);
      setHoveredFile(ofxFile);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setHoveredFile(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer.files);
    const ofxFile = files.find(file =>
      file.name.toLowerCase().endsWith('.ofx')
    );

    if (ofxFile) {
      handleFileSelect(ofxFile);
    }

    setIsDragOver(false);
    setHoveredFile(null);
  }, []);

  const formatFileSize = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <Card className={cn("relative overflow-hidden", className)}>
      <CardContent className="p-0">
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-300
            ${isDragOver && !isProcessing
              ? `${stageConfig.borderColor} ${stageConfig.bgColor} scale-[1.02]`
              : 'border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/25'
            }
            ${isProcessing ? 'cursor-not-allowed' : ''}
          `}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isProcessing && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".ofx"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={isProcessing}
          />

          {/* Background gradient for active state */}
          {isDragOver && !isProcessing && (
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-50 rounded-lg" />
          )}

          <div className="relative z-10 space-y-4">
            {/* Icon */}
            <div className={`
              mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300
              ${isDragOver && !isProcessing ? 'scale-110' : ''}
              ${stageConfig.bgColor}
            `}>
              <CurrentIcon className={cn("h-8 w-8 transition-colors", stageConfig.color)} />
            </div>

            {/* Text content */}
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">
                {isProcessing ? 'Processando arquivo...' : 'Envie seu extrato OFX'}
              </h3>

              {!isProcessing ? (
                <p className="text-muted-foreground">
                  Arraste e solte ou clique para selecionar
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {currentStage === 'uploading' && 'Enviando arquivo...'}
                    {currentStage === 'parsing' && 'Analisando estrutura OFX...'}
                    {currentStage === 'processing' && 'Processando transações...'}
                    {currentStage === 'completed' && 'Processamento concluído!'}
                  </p>
                </div>
              )}
            </div>

            {/* File info on hover */}
            {hoveredFile && !isProcessing && (
              <div className="p-3 bg-card border rounded-lg shadow-sm">
                <p className="font-medium text-sm">{hoveredFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatFileSize(hoveredFile.size)}
                </p>
              </div>
            )}

            {/* Processing Progress */}
            {isProcessing && (
              <div className="space-y-3">
                <div className="flex justify-center">
                  <Badge variant="outline" className={stageConfig.borderColor}>
                    {currentStage === 'uploading' && 'Enviando'}
                    {currentStage === 'parsing' && 'Analisando'}
                    {currentStage === 'processing' && 'Processando'}
                    {currentStage === 'completed' && 'Concluído'}
                  </Badge>
                </div>

                <div className="w-full max-w-xs mx-auto">
                  <Progress
                    value={processingProgress}
                    className="w-full h-2"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {processingProgress.toFixed(0)}% completo
                  </p>
                </div>
              </div>
            )}

            {/* Quick actions */}
            {!isProcessing && (
              <div className="flex justify-center gap-3 pt-4">
                <Button variant="outline" size="sm">
                  <FileText className="h-4 w-4 mr-2" />
                  Exemplo de Arquivo
                </Button>
                <Button size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Selecionar Arquivo
                </Button>
              </div>
            )}

            {/* Requirements */}
            {!isProcessing && (
              <div className="text-xs text-muted-foreground space-y-1 pt-4 border-t">
                <p>• Formato: .ofx</p>
                <p>• Tamanho máximo: {formatFileSize(maxSize)}</p>
                <p>• Bancos suportados: Todos os principais bancos brasileiros</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}